import { ConfigurableProperty, DigitalTwin, DigitalTwinSettingTarget } from 'api/digitalTwin/digitalTwin.api'
import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { ReactNode, useRef } from 'react'
import LoadingPlaceholderContainer from 'ui/atoms/LoadingPlaceholderContainer/LoadingPlaceholderContainer'
import PreviousSettingsTable from 'ui/components/DigitalTwinEditSettingsModal/PreviousSettingsTable/PreviousSettingsTable'
import { usePropertySettings } from 'ui/components/DigitalTwinSettingEditor/usePropertySettings'
import { SettingsPermissions } from 'ui/components/SettingsTable/SettingsTable'
import { useIsNarrowerThan } from './useIsNarrowerThan'

/** Below this width the settings show as cards instead of tables. */
const SETTINGS_CARDS_BELOW_WIDTH_PX = 720

type PropertySettingsProps = {
  property: DigitalTwinSettingTarget
  revision: number
  onEditSetting: (setting: DigitalTwinSetting) => void
  digitalTwin: DigitalTwin
  configurableProperties: ConfigurableProperty[]
  priorities: SystemSettingPriorityLevel[]
  permissions: SettingsPermissions
  isBaseSettings: boolean
}

/** The active, upcoming and previous settings of the property the split view is open for. */
export function PropertySettings({
  property,
  revision,
  onEditSetting,
  digitalTwin,
  configurableProperties,
  priorities,
  permissions,
  isBaseSettings,
}: PropertySettingsProps): ReactNode {
  const { settings, isPending, operationalEvents, refetchOperationalEvents } = usePropertySettings({
    digitalTwin,
    configurableProperties,
    property,
    priorities,
    refetchKey: revision,
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const isCompact = useIsNarrowerThan(containerRef, SETTINGS_CARDS_BELOW_WIDTH_PX)

  return (
    <div className="px-6">
      <div ref={containerRef}>
        {isPending ? (
          <LoadingPlaceholderContainer height={200} />
        ) : (
          <PreviousSettingsTable
            compact={isCompact}
            allSettingsForChosenProperty={settings}
            operationalEvents={operationalEvents}
            onEventPostModalClose={refetchOperationalEvents}
            onEditSetting={onEditSetting}
            permissions={permissions}
            isBaseSettings={isBaseSettings}
            priorities={priorities}
          />
        )}
      </div>
    </div>
  )
}
